/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import React, { FC, ComponentProps, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useField } from 'formik';
import Flags from '@airwallex/flags';
import { Select } from '@airwallex/forms';
import { space, countryNames, colors } from '@airwallex/global';

import denyCountries from '@/constants/deny-countries';
import { Input } from '..';

export interface AddressProps {
  label?: string;
  name: string;
}

const removePadding = css`
  padding: 0;
`;

const Address: FC<AddressProps> = ({ label, name }) => {
  const { i18n, t } = useTranslation();
  const [streetAddressField, , streetAddressHelper] = useField<string>(`${name}.streetAddress`);
  const [cityField, , cityHelper] = useField<string>(`${name}.city`);
  const [stateField, , stateHelper] = useField<string>(`${name}.state`);
  const [countryField, countryMeta, countryHelper] = useField<string>(`${name}.country`);
  const countryError = countryMeta.error && countryMeta.touched;
  const [postCodeField, , postCodeHelper] = useField<string>(`${name}.postCode`);
  const countryOptions: ComponentProps<typeof Select>['options'] = Object.entries(countryNames)
    .filter(([code]) => !denyCountries.includes(code))
    .map(([code, i18nName]) => ({
      name: i18nName[i18n.language as 'zh' | 'en'],
      value: code,
    }));
  return (
    <div
      css={css`
        margin-top: ${space.s};
        .airwallex-select__control {
          border-color: ${countryError && colors.reds[50]};
        }
      `}
    >
      <Input
        name={`${name}.streetAddress`}
        label={label}
        placeholder={t('pay_by_link.form.street_address.placeholder')}
        value={streetAddressField.value}
        maxLength={1000}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          streetAddressHelper.setValue(e.target.value);
          streetAddressHelper.setTouched(true);
        }}
      />
      <div
        css={css`
          padding: 0 ${space.xs};
          display: flex;
          column-gap: ${space.xs};
          & > div {
            margin-bottom: 0;
            width: 50%;
          }
        `}
      >
        <Input
          css={removePadding}
          name={`${name}.city`}
          placeholder={t('pay_by_link.form.city.placeholder')}
          value={cityField.value}
          maxLength={100}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            cityHelper.setValue(e.target.value);
            cityHelper.setTouched(true);
          }}
        />
        <Input
          css={removePadding}
          name={`${name}.state`}
          placeholder={t('pay_by_link.form.state.placeholder')}
          value={stateField.value}
          maxLength={100}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            stateHelper.setValue(e.target.value);
            stateHelper.setTouched(true);
          }}
        />
      </div>
      <div
        css={css`
          padding: 0 ${space.xs};
          margin-top: ${space.xs};
          display: flex;
        `}
      >
        <div
          css={css`
            width: 50%;
            & > div {
              margin-bottom: 0;
            }
          `}
        >
          <Select
            css={css`
              .airwallex-select__placeholder {
                color: ${colors.greys[50]};
              }
            `}
            name={`${name}.country`}
            placeholder={t('pay_by_link.form.country.placeholder')}
            value={countryField.value}
            options={countryOptions}
            searchable
            formatOptionLabel={(option) => {
              const { name: countryName, value } = option;
              return (
                <div
                  css={css`
                    display: flex;
                    align-content: center;
                  `}
                >
                  <Flags
                    css={css`
                      width: 28px;
                      height: 20px;
                    `}
                    country={value}
                  />
                  <span
                    css={css`
                      margin-left: ${space.xs};
                    `}
                  >
                    {countryName}
                  </span>
                </div>
              );
            }}
            onChange={(value: string) => {
              countryHelper.setValue(value);
              countryHelper.setTouched(true);
            }}
          />
        </div>
        <div
          css={css`
            margin-left: ${space.xs};
            width: 50%;
            & > div {
              margin-bottom: 0;
            }
            label {
              margin-bottom: 0;
            }
          `}
        >
          <Input
            css={removePadding}
            name={`${name}.postCode`}
            placeholder={t('pay_by_link.form.post_code.placeholder')}
            value={postCodeField.value}
            maxLength={10}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              postCodeHelper.setValue(e.target.value);
              postCodeHelper.setTouched(true);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Address;
